import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import {
  NEWS_ARTICLE_SLUGS,
  isValidNewsSlug,
  parseNewsSlugFromPath,
} from '../data/newsArticles';
import './NewsPage.css';

function ArticleMeta({ meta }) {
  if (!meta || typeof meta !== 'object') return null;
  const { date, readTime, author } = meta;
  if (!date && !readTime && !author) return null;
  return (
    <div className="news-article-meta">
      {date && <span>{date}</span>}
      {readTime && (
        <>
          {date ? <span aria-hidden className="news-meta-sep">·</span> : null}
          <span>{readTime}</span>
        </>
      )}
      {author && (
        <>
          {(date || readTime) ? <span aria-hidden className="news-meta-sep">·</span> : null}
          <span>{author}</span>
        </>
      )}
    </div>
  );
}

function MedicalDisclaimer({ title, text }) {
  return (
    <aside className="news-disclaimer" role="note">
      <p className="news-disclaimer-title">
        <i className="fas fa-info-circle" aria-hidden="true" /> {title}
      </p>
      <p className="news-disclaimer-text">{text}</p>
    </aside>
  );
}

const NewsPage = () => {
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState(() => parseNewsSlugFromPath(window.location.pathname));

  const syncFromUrl = useCallback(() => {
    const slug = parseNewsSlugFromPath(window.location.pathname);
    if (slug && !isValidNewsSlug(slug)) {
      window.history.replaceState({}, '', '/noticias');
      setActiveSlug(null);
      return;
    }
    setActiveSlug(slug);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [syncFromUrl]);

  const articles = useMemo(() => (
    NEWS_ARTICLE_SLUGS.map((slug) => {
      const base = t(`news.articles.${slug}`, { returnObjects: true });
      if (!base || typeof base !== 'object' || Array.isArray(base)) {
        return null;
      }
      return { slug, ...base };
    }).filter(Boolean)
  ), [t]);

  const activeArticle = useMemo(
    () => articles.find((a) => a.slug === activeSlug),
    [articles, activeSlug]
  );

  const openArticle = (slug) => {
    window.history.pushState({}, '', `/noticias/${slug}`);
    setActiveSlug(slug);
    window.scrollTo(0, 0);
  };

  const backToList = () => {
    window.history.pushState({}, '', '/noticias');
    setActiveSlug(null);
    window.scrollTo(0, 0);
  };

  const paras = activeArticle?.paragraphs;
  const paragraphs = Array.isArray(paras) ? paras : [];

  return (
    <div className="news-page">
      <Header />
      <div className="news-page-inner">
        <header className="news-page-header">
          <p className="news-page-tag">{t('news.tag')}</p>
          <h1 className="news-page-title">{t('news.title')}</h1>
          <p className="news-page-lead">{t('news.lead')}</p>
        </header>

        {!activeSlug && (
          <div className="news-grid">
            {articles.map((article, index) => (
              <article
                key={article.slug}
                className={`news-card${index === 0 ? ' news-card--featured' : ''}`}
              >
                {article.category && (
                  <span className="news-card-category">{article.category}</span>
                )}
                <ArticleMeta meta={article.meta} />
                <h2 className="news-card-title">{article.title}</h2>
                <p className="news-card-excerpt">{article.excerpt || article.lead}</p>
                <button
                  type="button"
                  className="news-card-cta"
                  onClick={() => openArticle(article.slug)}
                >
                  {t('news.readArticle')} <i className="fas fa-arrow-right" aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        )}

        {activeSlug && activeArticle && (
          <article className="news-article-detail">
            <button type="button" className="news-back" onClick={backToList}>
              <i className="fas fa-arrow-left" aria-hidden="true" /> {t('news.backToList')}
            </button>
            {activeArticle.category && (
              <span className="news-card-category">{activeArticle.category}</span>
            )}
            <ArticleMeta meta={activeArticle.meta} />
            <h2 className="news-article-title">{activeArticle.title}</h2>
            <p className="news-article-lead">{activeArticle.lead}</p>

            {activeArticle.showMedicalDisclaimer && (
              <MedicalDisclaimer
                title={t('news.medicalDisclaimerTitle')}
                text={t('news.medicalDisclaimerText')}
              />
            )}

            <div className="news-article-body">
              {paragraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>

            {activeArticle.showMedicalDisclaimer && (
              <MedicalDisclaimer
                title={t('news.medicalDisclaimerTitle')}
                text={t('news.medicalDisclaimerText')}
              />
            )}
          </article>
        )}

        {activeSlug && !activeArticle && (
          <div className="news-empty">
            <p>{t('news.notFound')}</p>
            <button type="button" className="news-card-cta" onClick={backToList}>
              {t('news.backToList')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
